import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Popper from "@material-ui/core/Popper";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import AppsIcon from "@material-ui/icons/Apps";
import { floatingToolbarStyles } from "./floatingToolbarStyles";

const useStyles = makeStyles(floatingToolbarStyles);

const MobileBadgeContext = React.createContext({ register: () => () => {} });

export function useMobileBadge(count) {
  const { register } = React.useContext(MobileBadgeContext);
  const idRef = React.useRef(Math.random().toString(36).slice(2));
  React.useEffect(() => {
    return register(idRef.current, count || 0);
  }, [register, count]);
}

export default function FloatingToolbar({ children }) {
  const classes = useStyles();
  const [mobileAnchor, setMobileAnchor] = React.useState(null);
  const [badges, setBadges] = React.useState({});

  const registerBadge = React.useCallback((id, count) => {
    setBadges((prev) => ({ ...prev, [id]: count }));
    return () =>
      setBadges((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
  }, []);

  const badgeCtx = React.useMemo(
    () => ({ register: registerBadge }),
    [registerBadge],
  );

  const validChildren = React.Children.toArray(children).filter(Boolean);
  const count = validChildren.length;
  if (count === 0) return null;

  const getPositionClass = (index) => {
    if (count === 1) return "only";
    if (index === 0) return "first";
    if (index === count - 1) return "last";
    return "middle";
  };

  const handleMobileToggle = (event) => {
    setMobileAnchor(mobileAnchor ? null : event.currentTarget);
  };

  const handleMobileClose = () => {
    setMobileAnchor(null);
  };

  const totalBadge = Object.values(badges).reduce((a, b) => a + b, 0);

  return (
    <MobileBadgeContext.Provider value={badgeCtx}>
      {/* Desktop */}
      <div className={classes.container}>
        {validChildren.map((child, index) =>
          React.cloneElement(child, {
            _position: getPositionClass(index),
          }),
        )}
      </div>

      {/* Mobile */}
      <div className={classes.mobileContainer}>
        <div className={classes.mobileTrigger} onClick={handleMobileToggle}>
          <AppsIcon className={classes.mobileTriggerIcon} />
          {totalBadge > 0 && <span className={classes.mobileBadgeDot} />}
        </div>
        <Popper
          open={Boolean(mobileAnchor)}
          anchorEl={mobileAnchor}
          transition
          placement="bottom-end"
          style={{ zIndex: 1101 }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: "right top" }}>
              <Paper className={classes.mobileDropdown}>
                <ClickAwayListener onClickAway={handleMobileClose}>
                  <div>
                    {validChildren.map((child, index) =>
                      React.cloneElement(child, {
                        _mobile: true,
                        _position: getPositionClass(index),
                        key: index,
                      }),
                    )}
                  </div>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    </MobileBadgeContext.Provider>
  );
}

FloatingToolbar.propTypes = {
  children: PropTypes.node,
};
